import { useForm } from '@inertiajs/react'
import { useEffect } from 'react'
import { Workflow } from 'lucide-react'
import { Button } from '@/components/ui/button'

export interface ProjectPraxisSnapshot {
  configured: boolean
  selected_organization_id: string | null
  selected_organization_name: string | null
  organization_name: string | null
  tenant: string | null
  options: { id: string; name: string; tenant: string }[]
  copy: Record<string, string>
}

export default function ProjectPraxisBinding({ projectId, snapshot }: { projectId: string; snapshot: ProjectPraxisSnapshot }) {
  const { copy } = snapshot
  const unavailable = snapshot.selected_organization_id && !snapshot.options.some(option => option.id === snapshot.selected_organization_id)
  const values = { organization_id: snapshot.selected_organization_id || '', expected_organization_id: snapshot.selected_organization_id || '' }
  const form = useForm({ project_praxis: values })
  useEffect(() => {
    const fresh = { organization_id: snapshot.selected_organization_id || '', expected_organization_id: snapshot.selected_organization_id || '' }
    form.setDefaults('project_praxis', fresh)
    form.setData('project_praxis', fresh)
    form.clearErrors()
  }, [projectId, snapshot.selected_organization_id])
  const save = (event: React.FormEvent) => {
    event.preventDefault()
    form.patch(`/dashboard/discovery/project_praxis_binding?project_id=${projectId}`, {
      preserveScroll: true,
    })
  }
  return <section className="mx-4 mb-6 rounded-xl border border-border bg-card p-5 md:mx-6" aria-labelledby={`project-praxis-${projectId}`}>
    <header className="flex items-center gap-2"><Workflow className="size-4 text-muted-foreground" /><h3 id={`project-praxis-${projectId}`} className="text-sm font-semibold">{copy.title}</h3></header>
    <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{copy.description}</p>
    <p className="mt-3 text-xs text-muted-foreground">{snapshot.configured ? `${copy.current}: ${snapshot.organization_name} · ${snapshot.tenant}` : copy.unconfigured}</p>
    <form className="mt-4 flex flex-wrap items-end gap-3" onSubmit={save}>
      <label className="min-w-56 flex-1 text-xs">{copy.destination}
        <select className="mt-2 h-9 w-full rounded-md border bg-background px-3 text-sm" value={form.data.project_praxis.organization_id}
          onChange={event => form.setData('project_praxis', { ...form.data.project_praxis, organization_id: event.target.value })}>
          <option value="">{copy.default}</option>
          {unavailable && <option value={snapshot.selected_organization_id || ''} disabled>{snapshot.selected_organization_name} · {copy.unavailable}</option>}
          {snapshot.options.map(option => <option key={option.id} value={option.id}>{option.name} · {option.tenant}</option>)}
        </select>
      </label>
      <Button type="submit" size="sm" variant="outline" disabled={form.processing || !form.isDirty}>{form.processing ? copy.saving : copy.save}</Button>
    </form>
    {Object.values(form.errors)[0] && <p role="alert" className="mt-3 text-xs text-destructive">{Object.values(form.errors)[0]}</p>}
    {!snapshot.options.length && <p className="mt-3 text-xs text-muted-foreground">{copy.empty}</p>}
    <p className="mt-3 text-xs leading-5 text-muted-foreground">{copy.scope}</p>
  </section>
}
